<template>
  <div>
    <div v-if="latestExportStatus" class="form-group">
      <label>{{ trans("admin.exports.status.label") }}</label>
      <div
        class="lg:tw-flex max-lg:tw-space-y-2 tw-gap-2 tw-items-center alert"
        :class="latestExportStatus === 'failed' ? 'alert-danger' : 'alert-info'"
      >
        <div class="tw-flex-1">
          {{ trans("admin.exports.status.message." + latestExportStatus) }}
        </div>
        <div class="tw-flex-2 text-right">
          <Button
            v-if="latestExportStatus === 'completed'"
            class="d-inline right"
            :disabled="isDownloading"
            @click="emit('downloadExport')"
          >
            {{ trans("admin.exports.status.message.download") }}
          </Button>
        </div>
      </div>
    </div>
    <div class="form-group">
      <label :for="exportTypeInputId">
        {{ trans("forms.exportTypeLabel") }}
      </label>
      <der-select
        :id="exportTypeInputId"
        v-model="selectedExportType"
        :data-placeholder="trans('forms.chooseExportPlaceholder')"
        :data-options="dataAvailableExports"
        :data-get-option-value="(option) => option.type"
        :data-get-option-label="
          (option) =>
            !option.header ? option.name : '--- ' + option.name + ' ---'
        "
        :data-get-option-selectable="(option) => !option.header"
        :clearable="false"
      />
    </div>
    <form v-if="!!selectedExport" @submit.prevent="onSubmit">
      <component
        :is="selectedExport.form"
        v-bind="computedExportProps"
        :data-form-errors="dataFormErrors"
      />
      <div class="form-group">
        <label :for="formatInputId">
          {{ trans("forms.exportFileFormat") }}
        </label>
        <der-select
          :id="formatInputId"
          v-model="selectedFormat"
          :data-placeholder="trans('forms.exportFileFormatPlaceHolder')"
          :data-options="[
            { value: 'xlsx', label: 'Excel (.xlsx)' },
            { value: 'csv', label: 'CSV (.csv)' },
          ]"
          :data-get-option-value="(option) => option.value"
          :data-get-option-label="(option) => option.label"
          :clearable="false"
        />
      </div>
      <required-fields-notice
        v-if="selectedExport.form.requiredFields"
        :required-fields="selectedExport.form.requiredFields"
        class="mt-3"
      />
      <div class="text-right m-t-10">
        <input type="hidden" name="type" :value="selectedExport.type" />
        <input type="hidden" name="format" :value="selectedFormat" />
        <Button :is-loading="isExporting">
          {{ trans("center.export") }}
        </Button>
      </div>
    </form>
  </div>
</template>
<script setup>
import _find from "lodash/fp/find";
import { computed, provide, ref, toRefs, watch } from "vue";
import { v4 as uuidV4 } from "uuid";
import Button from "../../../../components/Layout/Button.vue";
import DerSelect from "../../../../components/Utils/DerSelect.vue";
import {
  EXPORT_FORM_MODEL_KEY,
  EXPORT_ROLE_FEATURES_KEY,
} from "./filter/comp/useExportFormField";
import RequiredFieldsNotice from "./filter/comp/RequiredFieldsNotice.vue";

const exportFormModel = ref({});
provide(EXPORT_FORM_MODEL_KEY, exportFormModel);

const exportTypeInputId = uuidV4();
const formatInputId = uuidV4();
const selectedExportType = ref();
const selectedFormat = ref("xlsx");

// Reset shared form model when switching export type
watch(selectedExportType, () => {
  exportFormModel.value = {};
});

const props = defineProps({
  dataAvailableExports: {
    type: /** @type {Array<{ 
      type: string;
      name: string;
      header: boolean;
      requiredFields?: boolean;
      form: import("vue").Component;
      props?: Record<string, unknown>;
    }>} */ Array,
    required: true,
  },
  dataFormErrors: {
    type: Object,
    default: () => ({}),
  },
  dataAvailableCenters: {
    type: Array,
    default: () => [],
  },
  dataAvailableRegions: {
    type: Array,
    default: () => [],
  },
  dataAvailableProducts: {
    type: Array,
    default: () => [],
  },
  isExporting: {
    type: Boolean,
    default: false,
  },
  latestExportStatus: {
    type: String,
    default: null,
  },
  isDownloading: {
    type: Boolean,
    default: false,
  },
  dataRoleFeatures: {
    type: Object,
    default: () => ({
      showExcludeInactiveCenters: false,
      showExcludeCentersWithTestInName: false,
      showExcludeTestPatients: false,
    }),
  },
});
provide(
  EXPORT_ROLE_FEATURES_KEY,
  computed(() => ({
    ...props.dataRoleFeatures,
    ...(selectedExport.value?.formOptions ?? {}),
  })),
);
const {
  dataAvailableExports,
  dataAvailableCenters,
  dataAvailableRegions,
  dataAvailableProducts,
} = toRefs(props);

const selectedExport = computed(() =>
  _find({ type: selectedExportType.value }, dataAvailableExports.value),
);

const computedExportProps = computed(() => {
  if (!selectedExport.value) return {};
  const baseProps = selectedExport.value.props
    ? { ...selectedExport.value.props }
    : {};
  // Inject availableCenters/availableRegions if the form expects them
  if (
    selectedExport.value.form &&
    (selectedExport.value.form.props?.dataAvailableCenters !== undefined ||
      selectedExport.value.form.props?.["data-available-centers"] !== undefined)
  ) {
    baseProps.dataAvailableCenters = dataAvailableCenters.value;
  }
  if (
    selectedExport.value.form &&
    (selectedExport.value.form.props?.dataAvailableRegions !== undefined ||
      selectedExport.value.form.props?.["data-available-regions"] !== undefined)
  ) {
    baseProps.dataAvailableRegions = dataAvailableRegions.value;
  }
  if (
    selectedExport.value.form &&
    (selectedExport.value.form.props?.dataAvailableProducts !== undefined ||
      selectedExport.value.form.props?.["data-available-products"] !==
        undefined)
  ) {
    baseProps.dataAvailableProducts = dataAvailableProducts.value;
  }
  return baseProps;
});

const isExporting = computed(() => props.isExporting);
const isDownloading = computed(() => props.isDownloading);
const latestExportStatus = computed(() => props.latestExportStatus);

const emit = defineEmits({
  exportData: () => {},
  downloadExport: () => {},
});
const onSubmit = ($event) => {
  const fd = new FormData($event.target);
  // Merge reactive model values (from provide/inject) into FormData.
  // This ensures values from non-native components (e.g. toggles) are included.
  for (const [key, value] of Object.entries(exportFormModel.value)) {
    if (!fd.has(key)) {
      fd.set(key, value);
    }
  }
  emit("exportData", fd);
};
</script>
